import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, showNotification, NOTIFICATION_TYPES } from '../utils/apiClient';
import { API_ENDPOINTS, STORAGE_KEYS } from '../utils/constants';
import {
  UserIcon,
  PencilIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
   ShieldExclamationIcon,
  ShieldCheckIcon,
  CameraIcon,
  XMarkIcon,
  ArrowPathIcon,
  BuildingLibraryIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon,
  HomeIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';
import DeleteAccountButton from '../components/profile/DeleteAccountButton';


const Profile = () => {
  const { user, isAuthenticated, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const hasFetchedRef = useRef(false);
  const abortControllerRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    accountNumber: '',
    ifsc: '',
    upiId: '',
    alternateNumber: '',
    profile: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }

    if (hasFetchedRef.current) return;
    loadProfile();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isAuthenticated, navigate]);

  const loadProfile = async () => {
    hasFetchedRef.current = true;
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      if (!token) {
        navigate('/login', { replace: true });
        return;
      }

      const result = await api.get(API_ENDPOINTS.AUTH.PROFILE, {
        signal: abortControllerRef.current.signal
      });

      let userData = null;
      if (result.success && result.data) {
        userData = result.data.data || result.data;
      }

      if (!userData || !userData.id) {
        throw new Error('Invalid response structure from server');
      }

      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
      setProfileData(userData);
      setFormData({
        name: userData.name || '',
        address: userData.address || '',
        accountNumber: userData.accountNumber || '',
        ifsc: userData.ifsc || '',
        upiId: userData.upiId || '',
        alternateNumber: userData.alternateNumber || '',
        profile: userData.profile || ''
      });
      setPreviewImage(userData.profile || null);

      if (updateUserProfile) {
        updateUserProfile(userData);
      }

    } catch (err) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
      
      const status = err.response?.status;
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load profile';
      
      if (status === 401) {
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
        showNotification('Session expired', NOTIFICATION_TYPES.ERROR);
        setTimeout(() => navigate('/login', { replace: true }), 1500);
        return;
      }
      
      setError(errorMsg);
      showNotification(errorMsg, NOTIFICATION_TYPES.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    validateField(name, value);
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateField = (fieldName, value) => {
    const errors = { ...validationErrors };

    switch (fieldName) {
      case 'name':
        if (value && value.trim().length > 0 && value.trim().length < 2) {
          errors.name = 'Name must be at least 2 characters';
        } else if (value && value.trim().length > 0 && !/^[a-zA-Z\s]+$/.test(value.trim())) {
          errors.name = 'Name can only contain letters and spaces';
        } else {
          delete errors.name;
        }
        break;
      case 'ifsc':
        if (value && value.trim() && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.trim().toUpperCase())) {
          errors.ifsc = 'Invalid IFSC code';
        } else {
          delete errors.ifsc;
        }
        break;
      case 'accountNumber':
        if (value && value.trim() && !/^\d{9,18}$/.test(value.trim())) {
          errors.accountNumber = 'Invalid account number';
        } else {
          delete errors.accountNumber;
        }
        break;
      case 'upiId':
        if (value && value.trim() && !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+$/.test(value.trim())) {
          errors.upiId = 'Invalid UPI ID';
        } else {
          delete errors.upiId;
        }
        break;
      case 'alternateNumber':
        if (value && value.trim() && !/^[6-9]\d{9}$/.test(value.trim())) {
          errors.alternateNumber = 'Invalid phone number';
        } else {
          delete errors.alternateNumber;
        }
        break;
      default:
        break;
    }

    setValidationErrors(errors);
  };

  const handleImageClick = () => {
    setShowImageOptions(true);
  };

  const handleImageOptionSelect = (type) => {
    setShowImageOptions(false);
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('capture', type === 'camera' ? 'environment' : '');
      fileInputRef.current.click();
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showNotification('Please select an image file', NOTIFICATION_TYPES.ERROR);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showNotification('Image size should be less than 5MB', NOTIFICATION_TYPES.ERROR);
      return;
    }

    try {
      setUploadingImage(true);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);

      const formDataToSend = new FormData();
      formDataToSend.append('profile', file);

      const result = await api.post(API_ENDPOINTS.AUTH.UPLOAD_PROFILE_IMAGE, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = result.data?.profileImageUrl || result.data?.data?.profile;
      
      setFormData(prev => ({ ...prev, profile: imageUrl }));
      showNotification('Profile image uploaded successfully', NOTIFICATION_TYPES.SUCCESS);

      if (!isEditing) {
        await handleSaveProfile();
      }

    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to upload image';
      showNotification(errorMessage, NOTIFICATION_TYPES.ERROR);
      setPreviewImage(formData.profile || null);
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleSaveProfile = async () => {
    if (Object.keys(validationErrors).length > 0) {
      setError('Please fix validation errors');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const updateData = {
        name: formData.name?.trim() || null,
        address: formData.address?.trim() || null,
        accountNumber: formData.accountNumber?.trim() || null,
        ifsc: formData.ifsc?.trim()?.toUpperCase() || null,
        upiId: formData.upiId?.trim() || null,
        alternateNumber: formData.alternateNumber?.trim() || null,
        profile: formData.profile?.trim() || null
      };

      const result = await api.put(API_ENDPOINTS.AUTH.PROFILE, updateData);
      const updatedData = result.data?.data || result.data;

      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedData));
      setProfileData(updatedData);
      setFormData({
        name: updatedData.name || '',
        address: updatedData.address || '',
        accountNumber: updatedData.accountNumber || '',
        ifsc: updatedData.ifsc || '',
        upiId: updatedData.upiId || '',
        alternateNumber: updatedData.alternateNumber || '',
        profile: updatedData.profile || ''
      });
      
      setIsEditing(false);
      setSuccess('Profile updated successfully!');
      showNotification('Profile updated successfully', NOTIFICATION_TYPES.SUCCESS);
      
      if (updateUserProfile) {
        updateUserProfile(updatedData);
      }

    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update profile';
      setError(errorMessage);
      showNotification(errorMessage, NOTIFICATION_TYPES.ERROR);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (profileData) {
      setFormData({
        name: profileData.name || '',
        address: profileData.address || '',
        accountNumber: profileData.accountNumber || '',
        ifsc: profileData.ifsc || '',
        upiId: profileData.upiId || '',
        alternateNumber: profileData.alternateNumber || '',
        profile: profileData.profile || ''
      });
      setPreviewImage(profileData.profile || null);
    }
    setValidationErrors({});
    setError('');
    setSuccess('');
    setIsEditing(false);
  };

  const getVerificationBadge = (isVerified) => {
    if (isVerified === 1 || isVerified === true) {
      return (
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
          <ShieldCheckIcon className="h-4 w-4" />
          <span>Verified</span>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600 font-medium">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-sm w-full">
          <ExclamationTriangleIcon className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Profile</h3>
          <p className="text-sm text-gray-600 mb-4">Please try again</p>
          <button 
            onClick={loadProfile} 
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        
        {/* Success/Error Messages */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}
        
        {success && (
          <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-800">{success}</p>
            </div>
          </div>
        )}

        {/* Profile Header Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="relative h-32 bg-gradient-to-r from-blue-600 to-indigo-600">
            <div className="absolute inset-0 bg-black/5"></div>
          </div>

          <div className="relative px-6 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-6 -mt-16">
              
              {/* Profile Image */}
              <div className="relative flex-shrink-0">
                <div className="w-32 h-32 rounded-xl overflow-hidden border-4 border-white shadow-lg bg-gradient-to-br from-blue-50 to-indigo-50">
                  {uploadingImage ? (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                    </div>
                  ) : previewImage ? (
                    <img src={previewImage} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600">
                      <UserIcon className="h-16 w-16 text-white" />
                    </div>
                  )}
                </div>
                
                <button 
                  onClick={handleImageClick}
                  disabled={uploadingImage}
                  className="absolute bottom-0 right-0 p-2 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Change profile picture"
                >
                  <CameraIcon className="h-5 w-5" />
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>

              {/* Profile Info */}
              <div className="flex-1 sm:pb-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                      {profileData.name || 'Complete Your Profile'}
                    </h1>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                      <div className="flex items-center gap-1.5">
                        <PhoneIcon className="h-4 w-4" />
                        <span>+91 {profileData.phoneNumber}</span>
                      </div>
                      {profileData.email && (
                        <div className="flex items-center gap-1.5">
                          <EnvelopeIcon className="h-4 w-4" />
                          <span className="truncate max-w-[200px]">{profileData.email}</span>
                        </div>
                      )}
                      {getVerificationBadge(profileData.isDocumentVerified)}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    {!isEditing ? (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Edit Profile
                      </button>
                    ) : (
                      <>
                        <button 
                          onClick={handleCancelEdit} 
                          disabled={saving}
                          className="px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveProfile}
                          disabled={saving || Object.keys(validationErrors).length > 0}
                          className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {saving ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <CheckCircleIcon className="h-4 w-4" />
                              Save Changes
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Personal Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-50 rounded-lg">
                <IdentificationIcon className="h-5 w-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
            </div>
            
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Enter your full name"
                    className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                      isEditing 
                        ? 'bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500/20' 
                        : 'bg-gray-50 border-gray-200 text-gray-600'
                    } ${validationErrors.name ? 'border-red-300' : ''}`}
                  />
                </div>
                {validationErrors.name && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    {validationErrors.name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={profileData.phoneNumber ? `+91 ${profileData.phoneNumber}` : ''}
                    disabled
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                  />
                </div>
                <p className="mt-1.5 text-xs text-gray-500">Primary phone number (cannot be changed)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alternate Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DevicePhoneMobileIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    name="alternateNumber"
                    value={formData.alternateNumber}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Enter 10 digit number"
                    className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                      isEditing 
                        ? 'bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500/20' 
                        : 'bg-gray-50 border-gray-200 text-gray-600'
                    } ${validationErrors.alternateNumber ? 'border-red-300' : ''}`}
                  />
                </div>
                {validationErrors.alternateNumber && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    {validationErrors.alternateNumber}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <div className="relative">
                  <div className="absolute top-3 left-3 pointer-events-none">
                    <HomeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    rows={3}
                    placeholder="Enter your complete address"
                    className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-colors resize-none ${
                      isEditing 
                        ? 'bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500/20' 
                        : 'bg-gray-50 border-gray-200 text-gray-600'
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Banking Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-emerald-50 rounded-lg">
                <BuildingLibraryIcon className="h-5 w-5 text-emerald-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Banking Details</h2>
            </div>
            
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CreditCardIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Enter account number"
                    className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                      isEditing 
                        ? 'bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500/20' 
                        : 'bg-gray-50 border-gray-200 text-gray-600'
                    } ${validationErrors.accountNumber ? 'border-red-300' : ''}`}
                  />
                </div>
                {validationErrors.accountNumber && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    {validationErrors.accountNumber}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IFSC Code
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <BuildingLibraryIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="ifsc"
                    value={formData.ifsc}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="e.g., SBIN0001234"
                    className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg uppercase focus:outline-none focus:ring-2 transition-colors ${
                      isEditing 
                        ? 'bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500/20' 
                        : 'bg-gray-50 border-gray-200 text-gray-600'
                    } ${validationErrors.ifsc ? 'border-red-300' : ''}`}
                  />
                </div>
                {validationErrors.ifsc && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    {validationErrors.ifsc}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UPI ID
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <BanknotesIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="upiId"
                    value={formData.upiId}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="yourname@upi"
                    className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                      isEditing 
                        ? 'bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500/20' 
                        : 'bg-gray-50 border-gray-200 text-gray-600'
                    } ${validationErrors.upiId ? 'border-red-300' : ''}`}
                  />
                </div>
                {validationErrors.upiId && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    {validationErrors.upiId}
                  </p>
                )}
              </div>

              <div className="pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500 flex items-start gap-2">
                  <svg className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Your banking details are securely stored and used only for payment processing.</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        {/* Quick Actions */}
<div className="mt-6 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl shadow-sm p-6">
  <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <button 
      onClick={() => navigate('/document-verification')}
      className="flex items-center justify-center gap-2 py-3 px-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-lg font-medium text-sm transition-all border border-white/20"
    >
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      Upload Documents
    </button>
  </div>
</div>

{/* ✅ NEW: Danger Zone */}
<div className="mt-6 bg-white rounded-xl shadow-sm border border-red-200 p-6">
  <div className="flex items-center gap-3 mb-4">
    <div className="p-2 bg-red-50 rounded-lg">
      <ShieldExclamationIcon className="h-5 w-5 text-red-600" />
    </div>
    <div>
      <h3 className="text-lg font-semibold text-gray-900">Danger Zone</h3>
      <p className="text-sm text-gray-600">Irreversible actions</p>
    </div>
  </div>
  
  <div className="space-y-3">
    <DeleteAccountButton />
  </div>
</div>

      </div>

      {/* Image Upload Modal */}
      {showImageOptions && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md shadow-2xl animate-slide-up">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Choose Photo Source</h3>
                <button 
                  onClick={() => setShowImageOptions(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => handleImageOptionSelect('camera')}
                  className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  <CameraIcon className="h-5 w-5" />
                  Take Photo
                </button>

                <button
                  onClick={() => handleImageOptionSelect('gallery')}
                  className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Choose from Gallery
                </button>

                <button
                  onClick={() => setShowImageOptions(false)}
                  className="w-full px-6 py-3.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        @media (min-width: 640px) {
          .animate-slide-up {
            animation: fade-in 0.3s ease-out;
          }
        }
      `}</style>
    </div>
  );
};

export default Profile;